import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

const SOUND_KEY = "zap_admin_sound";
const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const toKey = (b64) => {
  const s = (b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
};

let ctx = null;
export const chime = () => {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    [[880, 0], [1320, 0.16]].forEach(([f, at]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      const t = ctx.currentTime + at;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.35, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + 0.5);
    });
  } catch { /* audio unavailable */ }
};

const usePulse = (api, soundOn, enabled) => {
  const last = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const tick = () => api.get("/admin/pulse").then(({ data }) => {
      if (!alive || !data.latest) return;
      if (last.current && data.latest > last.current) {
        if (soundOn) chime();
        toast(`Nova mensagem de ${data.name.split(" ")[0] || "participante"}`, {
          description: data.preview || undefined, duration: 30000,
          action: { label: "Abrir", onClick: () => nav(`/admin/conversas?p=${data.participant_id}`) },
        });
      }
      last.current = data.latest;
    }).catch(() => {});
    tick();
    const t = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [api, soundOn, nav, enabled]);
};

const usePush = (api, enabled) => {
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!enabled) return;
    if (!pushSupported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then(async (sub) => {
      if (!sub || Notification.permission !== "granted") { setState("off"); return; }
      const { data } = await api.post("/admin/push/status", { subscription: sub.toJSON() });
      setState(data.subscribed && !data.paused ? "on" : "off");
    }).catch(() => setState("off"));
  }, [api, enabled]);
  const toggle = async () => {
    if (state === "unsupported") { toast.info(isIOS() ? "No iPhone, instale o app da equipe na tela inicial e abra por ele para receber avisos." : "Este navegador não recebe avisos. Use o Chrome."); return; }
    if (state === "denied") { toast.error("Os avisos estão bloqueados neste navegador. Libere em Configurações do site → Notificações."); return; }
    try {
      if (Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") { setState("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await api.get("/public/push/key");
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) });
      }
      const paused = state === "on";
      await api.post("/admin/push/subscribe", { subscription: sub.toJSON(), paused });
      setState(paused ? "off" : "on");
      toast.success(paused ? "Avisos no celular pausados neste aparelho." : "Avisos ativados: você será avisado a cada mensagem nova.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return [state, toggle];
};

const Toggle = ({ on, onClick, IconOn, IconOff, label, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} aria-pressed={on} title={`${label}: ${on ? "ligado" : "desligado"}`}
    className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold transition-colors md:h-10 md:flex-1 md:justify-center md:text-sm ${on ? "border-zap-gold bg-zap-gold/20 text-white" : "border-white/25 text-white/60 hover:bg-white/10"}`}>
    {on ? <IconOn className="h-4 w-4 text-zap-gold" /> : <IconOff className="h-4 w-4" />}
    <span className="hidden md:inline">{label}</span>
    <span className={`hidden h-2 w-2 rounded-full md:inline-block ${on ? "bg-emerald-400" : "bg-white/30"}`} />
  </button>
);

export const useAdminAlerts = (api, enabled) => {
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== "off");
  usePulse(api, soundOn, enabled);
  const [push, togglePush] = usePush(api, enabled);
  const flipSound = () => {
    const v = !soundOn;
    setSoundOn(v); localStorage.setItem(SOUND_KEY, v ? "on" : "off");
    if (v) chime();
  };
  return { soundOn, flipSound, push, togglePush };
};

export const AlertControls = ({ alerts, where }) => (
  <div data-testid={`alert-controls-${where}`} className="flex items-center gap-1.5 md:mx-2 md:gap-2">
    <Toggle testId={`sound-toggle-${where}`} on={alerts.soundOn} onClick={alerts.flipSound} IconOn={Volume2} IconOff={VolumeX} label="Som" />
    <Toggle testId={`push-toggle-${where}`} on={alerts.push === "on"} onClick={alerts.togglePush} IconOn={Bell} IconOff={BellOff} label="Celular" />
  </div>
);
